import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Chip,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { Layout } from "../components/Layout";
import { DeletePatientModal } from "../components/DeletePatientModal";
import {
  patientService,
  Patient,
  PatientAssociation,
  CCMCareProvider,
} from "../services/patient.service";
import PatientDemographicsTab from "../components/patient/PatientDemographicsTab";
import PatientProvidersTab from "../components/patient/PatientProvidersTab";
import PatientSecurityTab from "../components/patient/PatientSecurityTab";
import PatientHealthTab from "../components/patient/PatientHealthTab";
import PatientOrdersTab from "../components/patient/PatientOrdersTab";
import { toLocaleDateString } from "../utils/dateUtils";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`patient-tabpanel-${index}`}
      aria-labelledby={`patient-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PatientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const patientId = parseInt(id || "0");
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // CCM Import state
  const CCM_ENTITY_IDS = [983, 1008, 2016];
  const [ccmAssociations, setCcmAssociations] = useState<PatientAssociation[]>(
    [],
  );
  const [ccmImportEntityId, setCcmImportEntityId] = useState<number>(0);
  const [ccmCareProviders, setCcmCareProviders] = useState<CCMCareProvider[]>(
    [],
  );
  const [ccmCareProvidersLoading, setCcmCareProvidersLoading] = useState(false);
  const [ccmImportCareProviderId, setCcmImportCareProviderId] =
    useState<string>("");
  const [ccmImporting, setCcmImporting] = useState(false);
  const [ccmImportResult, setCcmImportResult] = useState<{
    success: boolean;
    message: string;
    severity: "success" | "error" | "info";
  } | null>(null);

  const loadPatientData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [patientData, associations] = await Promise.all([
        patientService.getPatientById(parseInt(id)),
        patientService.getPatientAssociations(parseInt(id)),
      ]);
      setPatient(patientData);
      const ccm = (associations as PatientAssociation[]).filter((a) =>
        CCM_ENTITY_IDS.includes(a.entityID),
      );
      setCcmAssociations(ccm);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load patient");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatientData();
  }, [id]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDemographicsUpdate = (updatedData: Partial<Patient>) => {
    if (patient) {
      setPatient({ ...patient, ...updatedData });
    }
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async (
    firstName: string,
    lastName: string,
  ): Promise<void> => {
    if (!patient) return;

    await patientService.deletePatientAccount(patient.id, firstName, lastName);

    // Navigate back to patients list after successful deletion
    navigate("/patients");
  };

  const handleCCMEntityChange = async (entityId: number) => {
    setCcmImportEntityId(entityId);
    setCcmImportCareProviderId("");
    setCcmCareProviders([]);
    setCcmImportResult(null);
    if (!entityId) return;
    try {
      setCcmCareProvidersLoading(true);
      const providers = await patientService.getCCMCareProviders(entityId);
      setCcmCareProviders(providers);
    } catch {
      setCcmCareProviders([]);
    } finally {
      setCcmCareProvidersLoading(false);
    }
  };

  const handleCCMImport = async () => {
    if (!ccmImportEntityId) return;
    setCcmImporting(true);
    setCcmImportResult(null);
    try {
      const result = await patientService.importPatientToCCM(
        patientId,
        ccmImportEntityId,
        ccmImportCareProviderId || undefined,
      );
      if (result.success) {
        if (result.wasExisting) {
          setCcmImportResult({
            success: true,
            message:
              result.linked === false
                ? `Patient already exists in CCM (ID: ${result.ccmPatientId}). Care provider link failed: ${result.linkError}`
                : `Patient already exists in CCM (ID: ${result.ccmPatientId}). Care provider link verified.`,
            severity: result.linked === false ? "info" : "success",
          });
        } else {
          setCcmImportResult({
            success: true,
            message:
              result.linked === false
                ? `Patient created in CCM (ID: ${result.ccmPatientId}) but care provider link failed: ${result.linkError}`
                : `Patient successfully imported to CCM (ID: ${result.ccmPatientId}).`,
            severity: result.linked === false ? "info" : "success",
          });
        }
      } else {
        setCcmImportResult({
          success: false,
          message: result.partnerError || "Import failed.",
          severity: "error",
        });
      }
    } catch (err: any) {
      setCcmImportResult({
        success: false,
        message:
          err.response?.data?.error || "Failed to import patient to CCM.",
        severity: "error",
      });
    } finally {
      setCcmImporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display='flex' justifyContent='center' p={4}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error || !patient) {
    return (
      <Layout>
        <Alert severity='error'>{error || "Patient not found"}</Alert>
      </Layout>
    );
  }

  const age = patient.dob
    ? Math.floor(
        (new Date().getTime() - new Date(patient.dob).getTime()) /
          (365.25 * 24 * 60 * 60 * 1000),
      )
    : "N/A";

  return (
    <Layout>
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/patients")}
          sx={{ mb: 2 }}
        >
          Back to Patients
        </Button>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <Box>
              <Box display='flex' alignItems='center' gap={2}>
                <Typography variant='h4' gutterBottom>
                  {patient.lastname}, {patient.firstname}
                </Typography>
                {patient.isOnHold === 1 && (
                  <Chip
                    icon={<BlockIcon />}
                    label='Supply Hold'
                    color='error'
                    size='medium'
                  />
                )}
              </Box>
              <Typography variant='body1' color='text.secondary'>
                Chart ID: {patient.chartID} | Age: {age} |{" "}
                {patient.gender === "M"
                  ? "Male"
                  : patient.gender === "F"
                    ? "Female"
                    : "Unknown"}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                DOB: {toLocaleDateString(patient.dob) || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='patient tabs'
            >
              <Tab label='Demographics' />
              <Tab label='Providers' />
              <Tab label='Security' />
              <Tab label='Health' />
              <Tab label='Orders' />
              <Tab label='General' />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <PatientDemographicsTab
              patientId={patientId}
              onUpdate={handleDemographicsUpdate}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <PatientProvidersTab patientId={patientId} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <PatientSecurityTab patientId={patientId} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <PatientHealthTab patientId={patientId} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <PatientOrdersTab patientId={patientId} />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            {/* CCM Import */}
            <Paper sx={{ border: 1, borderColor: "primary.main", mb: 3 }}>
              <Box sx={{ p: 3 }}>
                <Box display='flex' alignItems='center' gap={1} mb={2}>
                  <CloudUploadIcon color='primary' />
                  <Typography variant='h6'>CCM Import</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant='body2' color='text.secondary' paragraph>
                  Manually import this patient into the CCM partner system for
                  the selected entity. This will create the patient in CCM (if
                  they don't already exist) and link them to the entity's care
                  provider.
                </Typography>

                {ccmImportResult && (
                  <Alert severity={ccmImportResult.severity} sx={{ mb: 2 }}>
                    {ccmImportResult.message}
                  </Alert>
                )}

                <Box
                  display='flex'
                  gap={2}
                  alignItems='flex-start'
                  flexWrap='wrap'
                >
                  <TextField
                    select
                    label='Select Client'
                    value={ccmImportEntityId || ""}
                    onChange={(e) =>
                      handleCCMEntityChange(Number(e.target.value))
                    }
                    sx={{ minWidth: 280 }}
                    size='small'
                  >
                    {ccmAssociations.length === 0 && (
                      <MenuItem value='' disabled>
                        No CCM-enabled clients found
                      </MenuItem>
                    )}
                    {ccmAssociations.map((a) => (
                      <MenuItem key={a.entityID} value={a.entityID}>
                        {a.entityName || `Entity ${a.entityID}`}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label='Select Care Provider'
                    value={ccmImportCareProviderId}
                    onChange={(e) => setCcmImportCareProviderId(e.target.value)}
                    sx={{ minWidth: 260 }}
                    size='small'
                    disabled={!ccmImportEntityId || ccmCareProvidersLoading}
                    helperText={
                      ccmImportEntityId &&
                      !ccmCareProvidersLoading &&
                      ccmCareProviders.length === 0
                        ? "No care providers found"
                        : ccmCareProvidersLoading
                          ? "Loading..."
                          : "Optional — uses entity default if blank"
                    }
                  >
                    <MenuItem value=''>— Use entity default —</MenuItem>
                    {ccmCareProviders.map((cp) => (
                      <MenuItem key={cp._id} value={cp._id}>
                        {cp.fname} {cp.lname}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    variant='contained'
                    startIcon={
                      ccmImporting ? (
                        <CircularProgress size={18} color='inherit' />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    onClick={handleCCMImport}
                    disabled={!ccmImportEntityId || ccmImporting}
                    sx={{ mt: 0.25 }}
                  >
                    {ccmImporting ? "Importing..." : "Import to CCM"}
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Danger Zone */}
            <Paper sx={{ border: 2, borderColor: "error.main" }}>
              <Box sx={{ p: 3 }}>
                <Box display='flex' alignItems='center' gap={1} mb={2}>
                  <WarningIcon color='error' />
                  <Typography variant='h6' color='error'>
                    Danger Zone
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant='body2' color='text.secondary' paragraph>
                  Deleting this patient account is permanent and cannot be
                  undone. All associated data, including medical records,
                  readings, device data, provider associations, and historical
                  information will be permanently removed.
                </Typography>
                <Button
                  variant='outlined'
                  color='error'
                  onClick={handleDeleteClick}
                  startIcon={<WarningIcon />}
                >
                  Delete Patient Account
                </Button>
              </Box>
            </Paper>
          </TabPanel>
        </Paper>
      </Box>

      <DeletePatientModal
        open={deleteModalOpen}
        patient={patient}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Layout>
  );
};

export default PatientDetailPage;
